import React, { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { apiGet } from "../api"
import serverName from "../serverName";

export function applyPlaceholders(str) {
    if (!str) return ""

    const escapeHtml = s =>
        String(s).replace(/[&<>"']/g, m => ({ "&":"&amp;", "<":"&lt;", ">":"&gt;", '"':"&quot;", "'":"&#39;" }[m]))

    const filenameToAlt = url => {
        try {
            const name = url.split("/").pop().split("?")[0]
            return name.replace(/\.[a-z0-9]+$/i, "").replace(/[-_]+/g, " ").trim() || "image"
        } catch { return "image" }
    }

    let text = String(str)
    text = escapeHtml(text)

    text = text.replace(/%serverName%/g, escapeHtml(serverName))

    text = text.replace(
        /(https?:\/\/[^\s<>'"]+\.(?:png|jpe?g|gif|webp|svg)(?:\?[^\s<>'"]*)?)/gi,
        m => {
            const safeSrc = m.replace(/&/g, "&amp;").replace(/"/g, "&quot;")
            const alt = filenameToAlt(m)
            return `<img src="${safeSrc}" alt="${alt}" loading="lazy" decoding="async" style="max-width:100%;height:auto;display:block;margin-top:6px;margin-bottom:6px;border-radius:8px" sizes="(max-width: 768px) 100vw, 800px" />`
        }
    )

    text = text.replace(/\n/g, "<br />")

    text = text.replace(
        /\[\[(.+?)\]\]/g,
        `<span style="color:#d675f1;font-weight:700;">$1</span>`
    )

    return text
}


export default function Rules() {
    const [rules, setRules] = useState(null)
    const [loading, setLoading] = useState(true)

    const normalize = d => {
        if (!d?.sections) return d
        const sections = d.sections.map(s => {
            const items = (s.items || []).map(it => {
                if (typeof it === "string") return { rule_name: "", text: it }
                const rn = typeof it?.rule_name === "string" ? it.rule_name : ""
                const tx = typeof it?.text === "string" ? it.text : ""
                return { rule_name: rn, text: tx }
            })
            return { name: s.name, items }
        })
        return { ...d, sections }
    }

    useEffect(() => {
        apiGet(`/rules_get.php?ts=${Date.now()}`).then(r => {
            if (r?.ok && r.rules) setRules(normalize(r.rules))
            setLoading(false)
        })
    }, [])

    const containerVariants = {
        hidden: {},
        show: {
            transition: { staggerChildren: 0.06, delayChildren: 0.05 }
        }
    }

    const sectionVariants = dir => ({
        hidden: { opacity: 0, x: dir === "left" ? -40 : 40, y: 0 },
        show: {
            opacity: 1,
            x: 0,
            y: 0,
            transition: { type: "spring", stiffness: 70, damping: 14, mass: 0.8 }
        }
    })

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        show: { opacity: 1, y: 0, transition: { duration: 0.25 } }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <motion.div
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ repeat: Infinity, duration: 1.2 }}
                    className="text-white/70 text-xl"
                >
                    Načítám pravidla…
                </motion.div>
            </div>
        )
    }

    if (!rules) return null

    return (
        <motion.div
            className="max-w-5xl mx-auto px-6 py-24"
            variants={containerVariants}
            initial="hidden"
            animate="show"
        >
            <motion.h1
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="text-center text-5xl font-extrabold mb-14 bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent tracking-wide"
            >
                {applyPlaceholders(rules.title)}
            </motion.h1>

            <motion.div className="space-y-10" variants={containerVariants}>
                <div className="flex flex-wrap justify-center gap-4 my-10">
                    {rules.sections.map((scs, i) => (
                        <a
                            key={i}
                            href={"#" + scs.name.replaceAll(" ", "_").toLowerCase()}
                            className="px-4 py-2 rounded-lg bg-white/5 text-purple-300 font-semibold tracking-wide transition-all duration-300 hover:bg-purple-500/20 hover:text-white hover:scale-105"
                        >
                            {scs.name}
                        </a>
                    ))}
                </div>

                {rules.sections.map((sec, i) => {
                    const dir = i % 2 === 0 ? "left" : "right"
                    return (
                        <motion.div
                            id={sec.name.replaceAll(" ","_").toLowerCase()}
                            key={i}
                            variants={sectionVariants(dir)}
                            className="rounded-3xl p-8 bg-white/5 border border-white/10 backdrop-blur-xl shadow-xl relative overflow-hidden"
                        >
                            <div className="absolute -inset-1 rounded-3xl bg-gradient-to-tr from-purple-600/10 via-fuchsia-500/10 to-indigo-500/10" />
                            <motion.div className="relative" variants={containerVariants}>
                                <motion.h2
                                    className="text-2xl font-semibold tracking-wide mb-4 text-white"
                                    variants={itemVariants}
                                >
                                    {sec.name}
                                </motion.h2>
                                <motion.ul className="space-y-3 text-white/80">
                                    {sec.items.map((item, j) => (
                                        <motion.li
                                            key={j}
                                            className="flex items-start gap-3 group pb-5"
                                            variants={itemVariants}
                                        >
                                            <span className="mt-1 h-2 w-2 rounded-full bg-purple-400 group-hover:bg-white transition-all" />
                                            <span className="leading-relaxed">
                                                <div className="text-xl">
                                                    <b dangerouslySetInnerHTML={{ __html: applyPlaceholders(item.rule_name) }} />
                                                </div>
                                                <div
                                                    className="text-base"
                                                    dangerouslySetInnerHTML={{ __html: applyPlaceholders(item.text) }}
                                                />
                                            </span>
                                        </motion.li>
                                    ))}
                                </motion.ul>
                            </motion.div>
                        </motion.div>
                    )
                })}
            </motion.div>
        </motion.div>
    )
}
